import * as React from 'react';
import { type Editor } from '@tiptap/react';

import { useTiptapEditor } from 'containers/Tiptap/hooks/use-tiptap-editor';
import { isNodeInSchema } from 'containers/Tiptap/lib/tiptap-utils';

import { TableIcon } from 'containers/Tiptap/components/tiptap-icons/table-icon';

import type { ButtonProps } from 'containers/Tiptap/components/tiptap-ui-primitive/button';
import { Button } from 'containers/Tiptap/components/tiptap-ui-primitive/button';

export interface TableButtonProps extends ButtonProps {
  editor?: Editor | null;
}

export function insertTable(editor: Editor | null): boolean {
  if (!editor) return false;

  return editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: false }).run();
}

export const TableButton = React.forwardRef<HTMLButtonElement, TableButtonProps>(
  ({ editor: providedEditor, className = '', onClick, children, ...buttonProps }, ref) => {
    const { editor } = useTiptapEditor(providedEditor);

    const handleClick = React.useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(e);
        if (!e.defaultPrevented) {
          insertTable(editor);
        }
      },
      [onClick, editor]
    );

    if (!editor || !editor.isEditable || !isNodeInSchema('table', editor)) {
      return null;
    }

    return (
      <Button
        ref={ref}
        type="button"
        className={className.trim()}
        data-style="ghost"
        role="button"
        tabIndex={-1}
        aria-label="Insert table"
        tooltip="Insert table"
        onClick={handleClick}
        {...buttonProps}
      >
        {children || <TableIcon className="tiptap-button-icon" />}
      </Button>
    );
  }
);

TableButton.displayName = 'TableButton';

export default TableButton;
